import { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore, useGlobalStore } from '@/stores';
import { AmyGregoryDashboard, DefaultDashboard, DynamicDashboardV8 } from '@/features/dashboard';
import { useExperiments } from '@/features/Experiments/hooks';
import { EXPERIMENTS } from '@/features/Experiments/constants';
import { useUser } from '@/queries';
import { hasActiveSubscription } from '@/features/subscriptions/logic/subscriptionStatus.logic';
import { REFERRAL_DASHBOARD_TEMPLATE_NAMES } from '@/lib/constants';
import { StanBanner } from '@/components/core-components/StanBanner';
import { useIntegrationBanners } from '@/features/dashboard/hooks/useIntegrationBanners';
import IcAlertCircle from '@/assets/icons/ic-alert-circle.svg';

/**
 * Checks if referral pre-onboarding is done
 * Returns true if EITHER of these tags exist:
 * - user.tags.onboarding_referral_use_template
 * - user.tags.onboarding_referral_start_from_scratch
 */
function isReferralPreOnboardingDone(user: ReturnType<typeof useUser>['user']): boolean {
    if (!user?.tags) return false;
    return !!(user.tags.onboarding_referral_use_template || user.tags.onboarding_referral_start_from_scratch);
}

export default function DashboardPage() {
    const { isAuthenticated, storeInitialized } = useAuthStore();
    const setBreadcrumbs = useGlobalStore(state => state.setBreadcrumbs);
    const navigate = useNavigate();
    const { hasExperiment } = useExperiments();
    const { user } = useUser();
    const [searchParams] = useSearchParams();

    // Testing override: check query parameter for referral pre-onboarding override
    const referralPreOnboardingOverride = searchParams.get('referralPreOnboarding');

    // Get integration banner data - must be called before any early returns to follow Rules of Hooks
    const {
        hasStripeAccountIssues,
        stripeAccountErrorBannerMessage,
        manageStripeAccount,
        hasPaypalAccountIssues,
        paypalAccountErrorBannerMessage,
        paypalButton,
        hasProductWithErroredIntegration,
        missingIntegrationErrorBannerMessage,
        isErroredIntegration,
        navigateToIntegrations,
        loading,
    } = useIntegrationBanners();

    // Set breadcrumbs for dashboard section
    useEffect(() => {
        setBreadcrumbs([{ title: 'Home', to: '/dashboard', active: true, id: 'home' }]);
    }, [setBreadcrumbs]);

    // Redirect to login if not authenticated
    useEffect(() => {
        if (!isAuthenticated && storeInitialized) {
            navigate('/login', { replace: true });
            return;
        }
    }, [isAuthenticated, navigate, storeInitialized]);

    // Check if should show Amy Gregory dashboard
    const showAmyGregoryDashboard = useMemo(() => {
        // Testing override: if query param is set to AMY_GREGRORY, force show the dashboard
        if (referralPreOnboardingOverride === REFERRAL_DASHBOARD_TEMPLATE_NAMES.AMY_GREGRORY) {
            return true;
        }

        if (!user) return false;

        // User status is active (user.status !== 0)
        // Status can be string or number, check both
        const statusValue = typeof user.status === 'string' ? parseInt(user.status, 10) : user.status;
        const isUserActive = statusValue !== 0 && !isNaN(statusValue);

        // User has active subscription
        const userHasActiveSubscription = hasActiveSubscription(user);

        // User hasn't completed referral onboarding (no completion tags)
        const referralOnboardingNotDone = !isReferralPreOnboardingDone(user);

        // user.use_referral_template_dashboard === REFERRAL_DASHBOARD_TEMPLATE_NAMES.AMY_GREGRORY
        const isAmyGregoryReferral =
            user.use_referral_template_dashboard === REFERRAL_DASHBOARD_TEMPLATE_NAMES.AMY_GREGRORY;

        return isUserActive && userHasActiveSubscription && referralOnboardingNotDone && isAmyGregoryReferral;
    }, [user, referralPreOnboardingOverride]);

    // Conditionally render dashboard based on conditions
    // Priority: Amy Gregory Dashboard > Dynamic Dashboard > Default Dashboard
    const DashboardComponent = useMemo(() => {
        if (showAmyGregoryDashboard) {
            return AmyGregoryDashboard;
        }
        if (hasExperiment(EXPERIMENTS.DYNAMIC_DASHBOARD_V8)) {
            return DynamicDashboardV8;
        }
        return DefaultDashboard;
    }, [showAmyGregoryDashboard, hasExperiment]);

    // Show loading if not authenticated (while redirecting)
    if (!isAuthenticated && storeInitialized) {
        return (
            <div className='flex min-h-screen items-center justify-center'>
                <div className='text-center'>
                    <div className='border-brand-main mx-auto size-8 animate-spin rounded-full border-b-2' />
                    <p className='text-gray mt-2'>Redirecting to login...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated && !storeInitialized) {
        return (
            <div className='flex min-h-screen items-center justify-center'>
                <div className='text-center'>
                    <div className='border-brand-main mx-auto size-8 animate-spin rounded-full border-b-2' />
                    <p className='text-gray mt-2'>Loading</p>
                </div>
            </div>
        );
    }

    // Render banners with priority: Stripe > PayPal > Product Integration Errors > Instagram
    const renderIntegrationBanners = () => {
        // Priority 1: Stripe account issues
        if (hasStripeAccountIssues) {
            return (
                <StanBanner
                    variant='warning'
                    title='Stripe Account Issue'
                    subtitle={
                        stripeAccountErrorBannerMessage || 'Your Stripe account has an issue that needs to be resolved.'
                    }
                    icon={IcAlertCircle}
                    buttons={[
                        {
                            name: loading ? 'Loading...' : 'Update Now',
                            onClick: manageStripeAccount,
                            color: 'primary',
                            variant: 'link',
                            disabled: loading,
                        },
                    ]}
                    className='mb-4'
                />
            );
        }

        // Priority 2: PayPal account issues
        if (hasPaypalAccountIssues) {
            return (
                <StanBanner
                    variant='warning'
                    title='PayPal Account Issue'
                    subtitle={
                        paypalAccountErrorBannerMessage || 'Your PayPal account has an issue that needs to be resolved.'
                    }
                    icon={IcAlertCircle}
                    buttons={[paypalButton]}
                    className='mb-4'
                />
            );
        }

        // Priority 3: Product with errored integration
        if (hasProductWithErroredIntegration) {
            return (
                <StanBanner
                    variant='warning'
                    title='Integration Error'
                    subtitle={missingIntegrationErrorBannerMessage || 'One of your products has an integration error.'}
                    icon={IcAlertCircle}
                    buttons={[
                        {
                            name: 'Go to Integrations',
                            onClick: navigateToIntegrations,
                            color: 'primary',
                            variant: 'link',
                        },
                    ]}
                    className='mb-4'
                />
            );
        }

        // Priority 4: Instagram integration error
        if (isErroredIntegration('instagram')) {
            return (
                <StanBanner
                    variant='warning'
                    title='Instagram Integration Error'
                    subtitle='Your Instagram integration needs to be reconnected.'
                    icon={IcAlertCircle}
                    buttons={[
                        {
                            name: 'Go to Integrations',
                            onClick: navigateToIntegrations,
                            color: 'primary',
                            variant: 'link',
                        },
                    ]}
                    className='mb-4'
                />
            );
        }

        return null;
    };

    return (
        <>
            {renderIntegrationBanners()}
            <DashboardComponent />
        </>
    );
}
